'use client'

import Elastica, {
  AxisAlignedBoundaryBox,
  initalConditionsPresets,
  useElastica,
} from '@darkroom.engineering/elastica/react'
import { useDrag } from '@use-gesture/react'
import { useCallback, useRef } from 'react'
import AE from '~/assets/ae-logo.svg'
import Legion from '~/assets/legion.svg'
import MachineGirl from '~/assets/machine-girl.svg'
import SecretSociety from '~/assets/secret-society.svg'
import XX from '~/assets/x-x.svg'
import { Image } from '~/components/image'
import { useStoryblokContext } from '~/integrations/storyblok/context'
import s from './background.module.css'

const config = {
  gridSize: 5,
  collisions: true,
  borders: 'rigid',
  play: true,
}

export function ContactBackground() {
  const isHovered = useRef(items.map(() => false))
  const {
    story: { content },
  } = useStoryblokContext()

  return (
    <div className="w-full h-full absolute inset-0 desktop-only">
      <Elastica
        config={config}
        initialCondition={initalConditionsPresets.random}
        update={({
          boxes,
          positions,
          velocities,
          externalForces,
          bounced,
          deltaTime,
        }) => {
          boxes.forEach(({ element }, index) => {
            const draggin = externalForces[index]

            const bounce = bounced[index]
            if (bounce % 2 !== 0) {
              element.classList.add(s.bounced)
            } else {
              element.classList.remove(s.bounced)
            }

            if (isHovered.current[index]) {
              velocities[index][0] = 0
              velocities[index][1] = 0
            }

            if (Math.abs(draggin[0] + draggin[1]) > 0) {
              velocities[index][0] = draggin[0] * 0.5
              velocities[index][1] = draggin[1] * 0.5
            }

            positions[index] = positions[index].map(
              (pos, i) => pos + velocities[index][i] * deltaTime
            )

            externalForces[index] = [0, 0]
          })
        }}
      >
        {content?.bounceIcons?.map(({ id, filename }, index) => (
          <Item key={id} index={index} isHovered={isHovered}>
            <Image
              src={filename}
              alt={filename}
              objectFit="contain"
              className="dr-w-80!"
            />
          </Item>
        ))}
      </Elastica>
    </div>
  )
}

function Item({ index, children, isHovered }) {
  const { elastica } = useElastica()
  const isDragging = useRef(false)

  const onDrag = useCallback(
    (newDir) => {
      const { externalForces } = elastica

      let norm = newDir.map((pos) => pos * pos).reduce((a, b) => a + b)
      norm = Math.sqrt(norm)

      if (norm === 0) return

      externalForces[index] = newDir.map((pos) => pos / norm)
    },
    [elastica, index]
  )

  const bind = useDrag(({ active, movement: [mx, my] }) => {
    if (active) {
      onDrag([mx, my])
      isDragging.current = true
      return
    }

    isDragging.current = false
  })

  return (
    <AxisAlignedBoundaryBox
      key={index}
      {...bind()}
      className="absolute inset-0 w-fit h-fit cursor-grab"
    >
      <div
        onMouseEnter={({ target }) => {
          isHovered.current[index] = true
        }}
        onMouseLeave={({ target }) => {
          isHovered.current[index] = false

          if (isDragging.current) return

          const { velocities } = elastica

          velocities[index] = [Math.random() - 0.5, Math.random() - 0.5]
        }}
      >
        {children}
      </div>
    </AxisAlignedBoundaryBox>
  )
}

const items = [
  {
    name: 'ae',
    icon: <AE className="fill-current" />,
  },
  {
    name: 'legion',
    icon: <Legion />,
  },
  {
    name: 'machine-girl',
    icon: <MachineGirl />,
  },
  {
    name: 'secret-society',
    icon: <SecretSociety />,
  },
  {
    name: 'x-x',
    icon: <XX />,
  },
]
